import { useEffect, useState } from "react";
import api, { formatApiError } from "@/api";
import { useLang } from "@/i18n";
import Layout from "@/components/Layout";
import { toast } from "sonner";
import { ShoppingBag, RefreshCw, Package, ExternalLink } from "lucide-react";

export default function CoachShop() {
  const { t } = useLang();
  const [products, setProducts] = useState([]);
  const [orders, setOrders] = useState([]);
  const [syncing, setSyncing] = useState(false);
  const [tab, setTab] = useState("products");

  const nav = [
    { to: "/coach", key: "dashboard", label: t("nav.dashboard"), end: true },
    { to: "/coach/clients", key: "clients", label: t("nav.clients") },
    { to: "/coach/exercises", key: "exercises", label: t("nav.exercises") },
    { to: "/coach/programs", key: "programs", label: t("nav.programs") },
    { to: "/coach/nutrition", key: "nutrition", label: t("nav.nutrition") },
    { to: "/coach/supplements", key: "supplements", label: t("nav.supplements") },
    { to: "/coach/stretching", key: "stretching", label: t("nav.stretching") },
    { to: "/coach/shop", key: "shop", label: t("nav.shop") },
    { to: "/coach/testimonials", key: "testimonials", label: t("nav.testimonials") },
    { to: "/coach/education", key: "education", label: t("nav.education") },
  ];

  const load = () => {
    api.get("/coach/shop/products").then((r) => setProducts(r.data)).catch(() => {});
    api.get("/coach/orders").then((r) => setOrders(r.data)).catch(() => {});
  };
  useEffect(() => { load(); }, []);

  const sync = async () => {
    setSyncing(true);
    try {
      const { data } = await api.post("/coach/printful/sync");
      toast.success(`${t("shop.synced")}: ${data.synced}`);
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setSyncing(false);
    }
  };

  const toggle = async (p) => {
    try {
      await api.patch(`/coach/shop/products/${p._id}`, { active: !p.active });
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const statusKey = (o) => (o.fulfillment_status && o.fulfillment_status !== "pending")
    ? `shop.status.${o.fulfillment_status}` : `shop.status.${o.payment_status}`;

  return (
    <Layout nav={nav}>
      <div className="max-w-4xl space-y-6" data-testid="coach-shop-page">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <h1 className="text-3xl font-black uppercase tracking-tight flex items-center gap-2">
            <ShoppingBag className="w-7 h-7 text-primary" /> {t("shop.title")}
          </h1>
          <button data-testid="shop-sync-button" onClick={sync} disabled={syncing}
            className="kc-btn-secondary !px-4 !py-2 text-sm inline-flex items-center gap-2">
            <RefreshCw className={`w-4 h-4 ${syncing ? "animate-spin" : ""}`} />
            {syncing ? t("common.loading") : t("shop.sync")}
          </button>
        </div>

        <div className="flex gap-2" data-testid="shop-tabs">
          <button data-testid="shop-tab-products" onClick={() => setTab("products")}
            className={`kc-badge !px-4 !py-2 cursor-pointer ${tab === "products" ? "bg-primary text-primary-foreground" : "bg-secondary text-muted-foreground"}`}>
            {t("shop.products")}
          </button>
          <button data-testid="shop-tab-orders" onClick={() => setTab("orders")}
            className={`kc-badge !px-4 !py-2 cursor-pointer ${tab === "orders" ? "bg-primary text-primary-foreground" : "bg-secondary text-muted-foreground"}`}>
            {t("shop.orders")} ({orders.length})
          </button>
        </div>

        {tab === "products" && (
          <div className="space-y-3" data-testid="coach-shop-products">
            {products.length === 0 && (
              <p className="text-sm text-muted-foreground">{t("shop.empty")}</p>
            )}
            {products.map((p) => (
              <div key={p._id} className="kc-card p-4 flex items-center gap-4" data-testid={`coach-shop-product-${p._id}`}>
                {p.thumbnail_url && (
                  <img src={p.thumbnail_url} alt={p.name} className="w-16 h-16 rounded-xl object-cover shrink-0" loading="lazy" />
                )}
                <div className="flex-1 min-w-0">
                  <p className="font-bold truncate">{p.name}</p>
                  <p className="text-xs text-muted-foreground mt-0.5">
                    {p.variants.length} {t("shop.size").toLowerCase()} · €{(Math.min(...p.variants.map((v) => v.retail_price)) || 0).toFixed(2)} ·
                    {" "}{p.variants.filter((v) => v.in_stock).length}/{p.variants.length} stock
                  </p>
                </div>
                <button data-testid={`coach-shop-toggle-${p._id}`} onClick={() => toggle(p)}
                  className={`kc-badge !px-3 !py-1.5 cursor-pointer shrink-0 ${p.active ? "bg-green-600/15 text-green-500" : "bg-secondary text-muted-foreground"}`}>
                  {p.active ? "ON" : "OFF"}
                </button>
              </div>
            ))}
          </div>
        )}

        {tab === "orders" && (
          <div className="space-y-3" data-testid="coach-shop-orders">
            {orders.length === 0 && (
              <div className="kc-card p-8 text-center" data-testid="coach-shop-no-orders">
                <Package className="w-8 h-8 text-muted-foreground mx-auto mb-2" />
                <p className="text-sm text-muted-foreground">{t("shop.noOrders")}</p>
              </div>
            )}
            {orders.map((o) => (
              <div key={o._id} className="kc-card p-4" data-testid={`coach-order-${o._id}`}>
                <div className="flex flex-wrap items-center justify-between gap-2">
                  <div className="min-w-0">
                    <p className="font-bold text-sm">{o.product_name} × {o.quantity}</p>
                    <p className="text-xs text-muted-foreground">
                      {o.email} · {new Date(o.created_at).toLocaleDateString()} · €{((o.amount_cents + (o.shipping_cents || 0)) / 100).toFixed(2)} · {o.provider}
                    </p>
                  </div>
                  <div className="flex items-center gap-2 shrink-0">
                    <span className={`kc-badge ${o.payment_status === "paid" ? "bg-green-600/15 text-green-500" : "bg-secondary text-muted-foreground"}`}>
                      {o.payment_status}
                    </span>
                    <span className="kc-badge bg-secondary text-muted-foreground" data-testid={`coach-order-status-${o._id}`}>
                      {t(statusKey(o))}
                    </span>
                  </div>
                </div>
                {o.tracking_url && (
                  <a href={o.tracking_url} target="_blank" rel="noreferrer"
                    className="text-xs font-bold text-primary hover:underline mt-2 inline-flex items-center gap-1">
                    <ExternalLink className="w-3.5 h-3.5" /> {t("shop.track")}
                  </a>
                )}
              </div>
            ))}
          </div>
        )}
      </div>
    </Layout>
  );
}
